/**
 * phraseMembershipMaps.ts
 *
 * The shared UUID-first translation-join membership builder. Extracted from
 * `useTranslatedRender` (PLAT-10457 A2) so BOTH the prose render hook and the
 * caption builder (`useTranslatedCaptionLines`) resolve a live source element
 * to its translation phrase through ONE implementation — id-first, positional
 * `initial_index` fallback — instead of two divergent copies. The load-bearing
 * PLAT-10611 semantics below are preserved verbatim.
 */

import type { TranslationPhrase } from '@src/types.ts/translation';

/**
 * PLAT-10611 — the UUID membership map for the translation join. Registers each
 * ACTIVE, non-empty phrase under every stable element UUID it covers
 * (`originalElementIds`, content-only). The render walk resolves a live element
 * by `source_uuid ?? id` against this map, so a clip element whose
 * `initial_index` has drifted from story space still joins its real phrase.
 * Retired (`active === false`) and index-less phrases are excluded so they never
 * shadow a live override.
 *
 * Only `uuid → phrase` (many elements to one phrase) is built — NOT
 * `uuid → element` / `uuid → group` (DESIGN §4.6): a reordered clip reuses the
 * same source element with the SAME `source_uuid` but a FRESH `id`, so an
 * element/group map would collide last-wins; the phrase map does not.
 *
 * PLAT-10646 — the positional `initial_index` fallback map was removed: every
 * live content phrase carries `originalElementIds`, and its content elements
 * always resolve via UUID, so the integer map only ever shadowed the id join.
 * PLAT-10457 follows the same id-only rule in the caption consumers.
 */
export function buildPhraseMembershipMaps(
  phrases: readonly TranslationPhrase[],
): {
  phraseByUuid: Map<string, TranslationPhrase>;
} {
  const phraseByUuid = new Map<string, TranslationPhrase>();
  for (const phrase of phrases) {
    if (phrase.originalIndices.length === 0) continue;
    if (phrase.active === false) continue;
    if (phrase.originalElementIds) {
      for (const id of phrase.originalElementIds) phraseByUuid.set(id, phrase);
    }
  }
  return { phraseByUuid };
}

/**
 * PLAT-11753 — every key a stored translation may have recorded this element
 * under, PROVENANCE FIRST.
 *
 * `source_uuid` is provenance ("which original word is this standing for") and
 * `id` is the token's own identity. For an ASR token they are the same string —
 * `ClipTranscriptSegment` backfills `source_uuid ??= id` — so nothing
 * distinguished them until "Replace Term" started minting a fresh `id` while
 * inheriting the replaced word's key into `source_uuid`
 * (`inheritedSourceUuidsFor`). The backend stamps `originalElementIds` from
 * `el.id` alone, so a translation produced AFTER such an edit records the minted
 * id while every consumer here looks the element up by `source_uuid` — the join
 * misses and the word is dropped from the render.
 *
 * Returning both keys lets a blob written either way resolve. Order is
 * load-bearing: minted ids come from a RECYCLING slot allocator
 * (`IdGenerator`, `HEX_START = 0x1000`), so after a delete-and-replace one
 * element can carry an inherited `source_uuid: '1001'` while a later element is
 * minted `id: '1001'`. Callers registering these into a map must therefore keep
 * the FIRST writer (see `AlignedBlocksView`'s position map), so provenance is
 * never shadowed by a recycled id.
 */
export function stableKeysOf(el: {
  id?: string;
  source_uuid?: string;
}): string[] {
  const provenance = el.source_uuid;
  const own = el.id;
  if (provenance && own && provenance !== own) return [provenance, own];
  const only = provenance ?? own;
  return only ? [only] : [];
}

/**
 * PLAT-11753 — resolve a live element to its phrase, tolerating either key (see
 * {@link stableKeysOf}). The single implementation both the prose hook and the
 * caption builder use; a bare `map.get(source_uuid ?? id)` at a call site is the
 * divergence this module exists to prevent.
 */
export function resolvePhraseForElement(
  el: { id?: string; source_uuid?: string },
  phraseByUuid: ReadonlyMap<string, TranslationPhrase>,
): TranslationPhrase | undefined {
  for (const key of stableKeysOf(el)) {
    const phrase = phraseByUuid.get(key);
    if (phrase) return phrase;
  }
  return undefined;
}
